import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import EnterpriseOperationalSection, {
  EnterpriseOperationalCard,
} from '../../components/enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { getActiveOrgId, listUserOrganizations, withOrganizationQuery } from '../../utils/orgUtils'
import { acceptOrgView } from '../../utils/orgContext.mjs'
import { buildCloudRequestUrl, cloudFailureCode } from '../../components/integrations/gcpDiscoverySession.mjs'

type CardState = {
  label: string
  detail: string
  to: string
}

function orgName(orgId: string | null) {
  if (!orgId) return ''
  return listUserOrganizations().find((item) => item.id === orgId)?.name || orgId
}

export default function IntegrationsOverview() {
  const { t } = useI18n()
  const { user, token } = useAuth()
  const userId = String(user?.id || '')
  const [orgId, setOrgId] = useState<string | null>(() => getActiveOrgId())
  const [cards, setCards] = useState<CardState[]>([])
  const [denied, setDenied] = useState(false)
  const viewRef = useRef({ generation: 0, userId, orgId })

  useEffect(() => {
    const apply = (next: string | null) => setOrgId((prev) => (prev === next ? prev : next))
    const onOrg = (event: Event) => {
      const detail = (event as CustomEvent<{ organizationId?: string | null }>).detail
      apply(detail && 'organizationId' in detail ? detail.organizationId || null : getActiveOrgId())
    }
    const onStorage = () => apply(getActiveOrgId())
    window.addEventListener('ds-active-organization-changed', onOrg)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('ds-active-organization-changed', onOrg)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    viewRef.current = { generation: viewRef.current.generation + 1, userId, orgId }
    setCards([])
    setDenied(false)
    if (!token || !userId) return
    const ticket = { ...viewRef.current }
    const headers = { Authorization: `Bearer ${token}` }
    const applyCards = (next: CardState[], permissionDenied = false) => {
      if (!acceptOrgView(ticket, viewRef.current)) return
      setDenied(permissionDenied)
      setCards(next)
    }
    void (async () => {
      const next: CardState[] = []
      let permissionDenied = false
      try {
        const providersRes = await fetch(withOrganizationQuery(API_ENDPOINTS.AI_PROVIDERS_V2), { headers })
        if (!acceptOrgView(ticket, viewRef.current)) return
        if (providersRes.status === 403) permissionDenied = true
        else if (providersRes.ok) {
          const body = await providersRes.json().catch(() => ({}))
          const count = Array.isArray(body?.data?.providers) ? body.data.providers.length : 0
          const mode = String(body?.data?.runtimeMode || '')
          next.push({
            label: t('integrations.nav.ai', 'AI Providers'),
            detail: mode
              ? t('integrations.overview.aiCount', '{{count}} configured · {{mode}}', { count, mode })
              : t('integrations.overview.notChecked', 'Not checked'),
            to: '/integrations/ai',
          })
        } else {
          next.push({
            label: t('integrations.nav.ai', 'AI Providers'),
            detail: t('integrations.overview.notChecked', 'Not checked'),
            to: '/integrations/ai',
          })
        }
      } catch {
        next.push({
          label: t('integrations.nav.ai', 'AI Providers'),
          detail: t('integrations.overview.notChecked', 'Not checked'),
          to: '/integrations/ai',
        })
      }

      try {
        const scannersRes = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_PROVIDERS), { headers })
        if (!acceptOrgView(ticket, viewRef.current)) return
        if (scannersRes.status === 403) permissionDenied = true
        else if (scannersRes.ok) {
          const body = await scannersRes.json().catch(() => ({}))
          const rows = Array.isArray(body?.data?.providers)
            ? body.data.providers
            : Array.isArray(body?.providers)
              ? body.providers
              : []
          const checked = rows.filter((row: { status?: string }) => row.status)
          const problems = checked.filter((row: { status?: string }) => row.status !== 'AVAILABLE')
          const note = String(problems.find((row: { statusMessage?: string }) => row.statusMessage)?.statusMessage || '')
          next.push({
            label: t('integrations.nav.scanners', 'Scanners'),
            detail: !rows.length
              ? t('integrations.overview.notChecked', 'Not checked')
              : !checked.length
                ? t('integrations.overview.notChecked', 'Not checked')
                : problems.length
                  ? t('integrations.overview.scannerCount', '{count} scanners · {problems} need attention', {
                      count: rows.length,
                      problems: problems.length,
                      note: note ? ` · ${note}` : '',
                    })
                  : t('integrations.overview.scannerRecorded', '{count} scanners · status recorded', { count: rows.length }),
            to: '/integrations/scanners',
          })
        } else {
          next.push({
            label: t('integrations.nav.scanners', 'Scanners'),
            detail: t('integrations.overview.notChecked', 'Not checked'),
            to: '/integrations/scanners',
          })
        }
      } catch {
        next.push({
          label: t('integrations.nav.scanners', 'Scanners'),
          detail: t('integrations.overview.notChecked', 'Not checked'),
          to: '/integrations/scanners',
        })
      }

      if (orgId) {
        try {
          const response = await fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', '', orgId), { headers })
          const body = await response.json().catch(() => ({}))
          if (!acceptOrgView(ticket, viewRef.current)) return
          if (cloudFailureCode(response.status, body) === 'ORGANIZATION_PERMISSION_DENIED') {
            permissionDenied = true
          } else if (response.ok) {
            const integrations = Array.isArray(body?.integrations) ? body.integrations : []
            const attention = integrations.filter((item: { lastTestResult?: { capabilities?: Array<{ status?: string; reason?: string | null }> } | null }) =>
              Array.isArray(item.lastTestResult?.capabilities)
              && item.lastTestResult.capabilities.some((capability) => capability.status && capability.status !== 'ok'),
            )
            const reason = attention
              .flatMap((item: { lastTestResult?: { capabilities?: Array<{ status?: string; reason?: string | null }> } }) => item.lastTestResult?.capabilities || [])
              .find((capability) => capability.status && capability.status !== 'ok' && capability.reason)?.reason
            const tested = integrations.some((item: { lastTestAt?: string | null }) => item.lastTestAt)
            next.push({
              label: t('integrations.nav.googleCloud', 'Google Cloud'),
              detail: !integrations.length
                ? t('gcpIntegration.empty', 'No Google Cloud connections for this organization.')
                : attention.length
                  ? t('integrations.overview.cloudAttention', '{count} connections · {problems} need attention{note}', {
                      count: integrations.length,
                      problems: attention.length,
                      note: reason ? ` · ${reason}` : '',
                    })
                  : tested
                    ? t('integrations.overview.cloudCount', '{count} connections · last test recorded', { count: integrations.length })
                    : t('integrations.overview.cloudUnchecked', '{count} connections · connection test not recorded', {
                        count: integrations.length,
                      }),
              to: '/integrations/google-cloud/connection',
            })
          } else {
            next.push({
              label: t('integrations.nav.googleCloud', 'Google Cloud'),
              detail: t('integrations.overview.notChecked', 'Not checked'),
              to: '/integrations/google-cloud/connection',
            })
          }
        } catch {
          next.push({
            label: t('integrations.nav.googleCloud', 'Google Cloud'),
            detail: t('integrations.overview.notChecked', 'Not checked'),
            to: '/integrations/google-cloud/connection',
          })
        }
      }
      applyCards(next, permissionDenied)
    })()
  }, [token, userId, orgId, t])

  return (
    <EnterpriseOperationalSection
      layerLabel={t('integrations.layerLabel', 'Platform integrations')}
      title={t('integrations.overview.title', 'Overview')}
      subtitle={orgId
        ? t('integrations.overview.org', 'Organization: {{name}}', { name: orgName(orgId) })
        : t('gcpIntegration.noOrg', 'Select an organization before managing Google Cloud.')}
      testId="integrations-overview"
    >
      {denied ? (
        <p className={entStyles.metaRow} data-testid="integrations-permission-denied">
          {t('integrations.overview.permission', 'You do not have permission to view one or more integration sources.')}
        </p>
      ) : null}
      <div className={entStyles.standardGrid}>
        {cards.map((card) => (
          <EnterpriseOperationalCard key={card.to} title={card.label}>
            <p className={entStyles.metaRow}>{card.detail}</p>
            <Link to={card.to}>{t('integrations.overview.open', 'Open')}</Link>
          </EnterpriseOperationalCard>
        ))}
      </div>
    </EnterpriseOperationalSection>
  )
}
